import { useEffect, useState } from 'react';

import { Edit as EditIcon, Undo as UndoIcon } from '@mui/icons-material';
import { Alert, Button, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { previewVoiceLoginId } from '@/lib/api/credentials';

interface Props {
  /** 아이디의 근거가 되는 값들 — 폼에서 고른 회원·호실 */
  patientCode: string | null;
  roomNumber: string | null;
  /** 직접 입력한 값. null이면 자동 발급을 쓴다 */
  value: string | null;
  onChange: (next: string | null) => void;
  error?: string;
}

/** 서버(assertValidVoiceLoginId)와 같은 규칙 — 저장 전에 같은 자리에서 걸러준다 */
export const VOICE_LOGIN_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{2,49}$/;
export const VOICE_LOGIN_ID_HINT = '영문 소문자·숫자로 시작하는 3~50자 (하이픈·밑줄 허용)';

/**
 * 발급될 보이스웰니스 아이디를 미리 보여주고, 원하면 직접 고치게 한다.
 *
 * 입력칸만 없으면 저장을 눌러봐야 어떤 아이디가 나오는지 알 수 있다. 근거(환자번호·
 * 호실)가 아예 없는 경우엔 발급이 거절되면서 등록까지 통째로 취소되는데, 그걸
 * 저장 버튼을 누른 뒤에 알게 되는 건 곤란하다.
 */
export function VoiceLoginIdField({ patientCode, roomNumber, value, onChange, error }: Props) {
  const [editing, setEditing] = useState(false);

  const preview = useQuery({
    queryKey: ['voice-login-id-preview', patientCode, roomNumber],
    queryFn: () => previewVoiceLoginId({ patientCode, roomNumber }),
    enabled: !!patientCode || !!roomNumber,
    staleTime: 10 * 1000,
  });

  const auto = (!patientCode && !roomNumber ? null : preview.data?.loginId) ?? null;
  const noBasis = auto === null && !preview.isFetching;

  // 근거가 없으면 직접 입력 외에 길이 없다. 사용자가 '수정'을 찾아 헤매지 않도록 연다.
  useEffect(() => {
    if (noBasis && value === null) setEditing(true);
  }, [noBasis, value]);

  if (editing || value !== null) {
    return (
      <Stack spacing={1}>
        <TextField
          label="보이스웰니스 아이디"
          size="small"
          value={value ?? auto ?? ''}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          error={!!error}
          helperText={error ?? VOICE_LOGIN_ID_HINT}
          slotProps={{ htmlInput: { autoCapitalize: 'none', spellCheck: false } }}
        />
        {auto !== null && (
          <Button
            size="small"
            startIcon={<UndoIcon />}
            onClick={() => {
              onChange(null);
              setEditing(false);
            }}
            sx={{ alignSelf: 'flex-start' }}
          >
            자동 발급({auto})으로 되돌리기
          </Button>
        )}
      </Stack>
    );
  }

  if (preview.isFetching) {
    return <Skeleton variant="rounded" height={32} />;
  }

  if (noBasis) {
    return (
      <Alert severity="warning" sx={{ py: 0.5 }}>
        환자번호나 호실이 있어야 아이디를 자동으로 만들 수 있습니다. 회원·호실을 고르거나 아이디를
        직접 입력해 주세요.
      </Alert>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2" color="text.secondary">
        발급될 아이디
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
        {auto}
      </Typography>
      <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
        수정
      </Button>
    </Stack>
  );
}
